import type { FormEvent, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { AdminMenuItemType } from '@cookspace-shared-contracts/core';

import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Switch } from '@/components/Switch';

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Предлагаемый дефолт порога при включении флага — редактируемый, НЕ хардкод. */
const SUGGESTED_MIN_AGE = '18';

interface MenuItemTypeFormState {
    name: string;
    slug: string;
    sortOrder: string;
    isActive: boolean;
    isAgeRestricted: boolean;
    minAge: string;
}

interface MenuItemTypeFormErrors {
    name?: string;
    slug?: string;
    minAge?: string;
}

export interface MenuItemTypeFormResult {
    name: string;
    slug: string;
    sortOrder: number;
    isActive: boolean;
    isAgeRestricted: boolean;
    /** Число при isAgeRestricted=true, иначе null (в запрос тогда не уходит). */
    minAge: number | null;
}

interface MenuItemTypeEditModalProps {
    isOpen: boolean;
    editing: AdminMenuItemType | null;
    /** Узел-родитель, под которым создаётся новый. null — корневой уровень. */
    parent: AdminMenuItemType | null;
    /** Унаследованный возрастной порог от родителя (effectiveMinAge родителя,
     * 0 — предки без ограничений). Read-only, считает сервер. */
    parentEffectiveMinAge: number;
    isSaving?: boolean;
    submitError?: string | null;
    onClose: () => void;
    onSubmit: (values: MenuItemTypeFormResult) => void;
}

const EMPTY_FORM: MenuItemTypeFormState = {
    name: '',
    slug: '',
    sortOrder: '0',
    isActive: true,
    isAgeRestricted: false,
    minAge: ''
};

export function MenuItemTypeEditModal({
    isOpen,
    editing,
    parent,
    parentEffectiveMinAge,
    isSaving = false,
    submitError = null,
    onClose,
    onSubmit
}: MenuItemTypeEditModalProps): ReactElement {
    const [form, setForm] = useState<MenuItemTypeFormState>(EMPTY_FORM);
    const [errors, setErrors] = useState<MenuItemTypeFormErrors>({});

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setErrors({});
        if (editing !== null) {
            setForm({
                name: editing.name,
                slug: editing.slug,
                sortOrder: String(editing.sortOrder),
                isActive: editing.isActive,
                isAgeRestricted: editing.isAgeRestricted,
                minAge: editing.minAge === null ? '' : String(editing.minAge)
            });
        } else {
            setForm({ ...EMPTY_FORM });
        }
    }, [isOpen, editing]);

    const validate = (): boolean => {
        const next: MenuItemTypeFormErrors = {};
        if (form.name.trim() === '') {
            next.name = 'Название обязательно';
        }
        if (form.slug.trim() === '') {
            next.slug = 'Slug обязателен';
        } else if (!SLUG_REGEX.test(form.slug)) {
            next.slug = 'Только латиница, цифры и дефисы';
        }
        if (form.isAgeRestricted) {
            // Зеркало контракта: isAgeRestricted=true ⇒ minAge обязателен
            // (число > 0; верхняя граница контракта — 150).
            const minAgeNum = Number(form.minAge);
            if (
                form.minAge.trim() === '' ||
                !Number.isInteger(minAgeNum) ||
                minAgeNum < 1 ||
                minAgeNum > 150
            ) {
                next.minAge = 'Укажите целое число от 1 до 150';
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleToggleAgeRestricted = (next: boolean): void => {
        setForm((prev) => ({
            ...prev,
            isAgeRestricted: next,
            // Предлагаем дефолт при включении, если порог ещё не задан.
            minAge: next && prev.minAge.trim() === '' ? SUGGESTED_MIN_AGE : prev.minAge
        }));
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (!validate()) {
            return;
        }
        const sortOrderNum = Number(form.sortOrder);
        onSubmit({
            name: form.name.trim(),
            slug: form.slug.trim(),
            sortOrder: Number.isFinite(sortOrderNum) ? sortOrderNum : 0,
            isActive: form.isActive,
            isAgeRestricted: form.isAgeRestricted,
            minAge: form.isAgeRestricted ? Number(form.minAge) : null
        });
    };

    let title = 'Новый корневой узел';
    if (editing !== null) {
        title = 'Редактировать узел';
    } else if (parent !== null) {
        title = `Новый дочерний узел: ${parent.name}`;
    }

    const contentDisplayMode = editing?.contentDisplayMode ?? 'standard';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            testId="menu-item-type-edit-modal"
        >
            <form
                onSubmit={handleSubmit}
                className="space-y-4"
                data-testid="menu-item-type-edit-form"
            >
                <Input
                    label="Название"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    error={errors.name}
                    data-testid="menu-item-type-form-name"
                    autoFocus
                />
                <Input
                    label="Slug"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="example-slug"
                    error={errors.slug}
                    data-testid="menu-item-type-form-slug"
                />
                <Input
                    label="Порядок сортировки"
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                    data-testid="menu-item-type-form-sort-order"
                />
                {editing !== null && (
                    <Switch
                        checked={form.isActive}
                        onChange={(next) => setForm({ ...form, isActive: next })}
                        label="Активен"
                        testId="menu-item-type-form-is-active"
                    />
                )}

                <div
                    className="space-y-3 rounded border border-amber-200 bg-amber-50 p-3"
                    data-testid="menu-item-type-form-age-block"
                >
                    <p className="text-xs font-medium uppercase text-amber-900">
                        Возрастное ограничение
                    </p>
                    {parentEffectiveMinAge > 0 && (
                        <p
                            className="text-xs text-amber-800"
                            data-testid="menu-item-type-form-inherited-hint"
                        >
                            Возрастное ограничение наследуется от родителя:{' '}
                            {parentEffectiveMinAge}+. Узел и все его потомки будут
                            ограничены минимум этим порогом независимо от собственного
                            флага.
                        </p>
                    )}
                    <Switch
                        checked={form.isAgeRestricted}
                        onChange={handleToggleAgeRestricted}
                        label="Собственное возрастное ограничение"
                        testId="menu-item-type-form-is-age-restricted"
                    />
                    {form.isAgeRestricted && (
                        <Input
                            label="Минимальный возраст"
                            type="number"
                            value={form.minAge}
                            onChange={(e) => setForm({ ...form, minAge: e.target.value })}
                            error={errors.minAge}
                            data-testid="menu-item-type-form-min-age"
                        />
                    )}
                    {editing !== null && (
                        <p
                            className="text-xs text-amber-800"
                            data-testid="menu-item-type-form-effective-min-age"
                        >
                            Итоговый порог узла (считает сервер):{' '}
                            {editing.effectiveMinAge > 0
                                ? `${editing.effectiveMinAge}+`
                                : 'без ограничений'}
                        </p>
                    )}
                </div>

                <label className="block">
                    <span className="mb-1 block text-sm text-gray-700">
                        Режим показа контента
                    </span>
                    <select
                        value={contentDisplayMode}
                        disabled
                        className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-500"
                        data-testid="menu-item-type-form-content-display-mode"
                    >
                        <option value="standard">Стандартный (фото и описание)</option>
                        <option value="text_only">Только текст (без фото)</option>
                    </select>
                    <span className="mt-1 block text-xs text-gray-500">
                        Строгий режим показа (текст без фото) — в разработке, вне MVP.
                    </span>
                </label>

                {submitError !== null && submitError !== '' && (
                    <div
                        className="text-sm text-red-600"
                        data-testid="menu-item-type-form-error"
                    >
                        {submitError}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        disabled={isSaving}
                        data-testid="menu-item-type-form-submit"
                        className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                    >
                        {editing !== null ? 'Сохранить' : 'Создать'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
